import { useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";

import type { Inserat } from "@/api/inserate";
import { inserateColumns } from "./columns";

interface Props {
  data: Inserat[];
}

export function InserateTable({ data }: Props) {
  const [sorting, setSorting] = useState<SortingState>([
    // Most recently updated first — matches the API default
    { id: "letzte_aktualisierung_at", desc: true },
  ]);

  const table = useReactTable({
    data,
    columns: inserateColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table
        className="w-full text-sm"
        data-testid="inserate-table"
      >
        <thead className="bg-muted/40">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sortDir = header.column.getIsSorted();
                const canSort = header.column.getCanSort();
                return (
                  <th
                    key={header.id}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    className={
                      "px-3 py-2 text-left font-medium text-muted-foreground" +
                      (canSort ? " cursor-pointer select-none" : "")
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {sortDir === "asc" && <span aria-hidden>▲</span>}
                      {sortDir === "desc" && <span aria-hidden>▼</span>}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-t hover:bg-muted/30"
              data-testid={`inserat-row-${row.original.key}`}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2 align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
